import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ReactNode, forwardRef, useState } from "react";
import { cx } from "../../utils/cx";
import { Button, ButtonSize, ButtonVariant } from "../Button/Button";
import styles from "./ButtonDropdown.module.scss";

export interface ButtonDropdownItem {
    /** Stable id; used as key + returned to onSelect when relevant. */
    id: string;
    /** Visible label. Can be a string or a custom node (icon + text, etc.). */
    label: ReactNode;
    /** Optional leading icon for the menu row. */
    icon?: ReactNode;
    /** Callback fired when the item is activated (click or Enter). */
    onSelect?: (id: string) => void;
    /** Renders the item as destructive (red). */
    destructive?: boolean;
    /** Disables the item. */
    disabled?: boolean;
}

export interface ButtonDropdownProps {
    /** Trigger label. */
    children: ReactNode;
    /** Optional icon before the label (same slot as Button `iconLeft`). */
    iconLeft?: ReactNode;
    /** Items rendered in the menu panel. */
    items: ButtonDropdownItem[];
    /** Visual hierarchy of the trigger (matches Button). Defaults to `primary`. */
    variant?: ButtonVariant;
    /** Trigger size (matches Button). Defaults to `md`. */
    size?: ButtonSize;
    /** Disables the trigger. */
    disabled?: boolean;
    /** Optional className forwarded to the trigger. */
    className?: string;
    /** Where the menu opens relative to the trigger. */
    align?: "start" | "center" | "end";
    /** Vertical placement. */
    side?: "top" | "right" | "bottom" | "left";
    /** Distance in px between trigger and menu. */
    sideOffset?: number;
    /** Optional aria-label for the trigger if `children` isn't textual. */
    "aria-label"?: string;
}

/**
 * Chevron rendered inside the trigger. Rotates 180° when the menu is open,
 * driven by `[data-state="open"]` that Radix sets on the trigger element.
 */
const Chevron = () => (
    <svg
        aria-hidden
        className={styles.chevron}
        fill="none"
        height="1em"
        viewBox="0 0 20 20"
        width="1em"
    >
        <path
            d="M5 7.5l5 5 5-5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.75"
        />
    </svg>
);

/**
 * Treez DS — ButtonDropdown (Wave 1).
 *
 * Composes `Button` as the trigger and `@radix-ui/react-dropdown-menu` for
 * the menu panel (a11y, keyboard nav, portaling, focus management).
 *
 * Visual: same primary/secondary/tertiary × sm/md/lg matrix as Button.
 * The right chevron flips when the menu opens (Figma `pressed` state shows
 * the chevron pointing up).
 */
export const ButtonDropdown = forwardRef<
    HTMLButtonElement,
    ButtonDropdownProps
>(function ButtonDropdown(
    {
        children,
        iconLeft,
        items,
        variant = "primary",
        size = "md",
        disabled,
        className,
        align = "start",
        side = "bottom",
        sideOffset = 6,
        "aria-label": ariaLabel,
    },
    ref,
) {
    const [open, setOpen] = useState(false);

    return (
        <DropdownMenu.Root onOpenChange={setOpen} open={open}>
            <DropdownMenu.Trigger asChild>
                <Button
                    aria-label={ariaLabel}
                    className={className}
                    disabled={disabled}
                    iconLeft={iconLeft}
                    iconRight={<Chevron />}
                    ref={ref}
                    size={size}
                    variant={variant}
                >
                    {children}
                </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align={align}
                    className={styles.content}
                    loop
                    side={side}
                    sideOffset={sideOffset}
                >
                    {items.map(item => (
                        <DropdownMenu.Item
                            className={cx(
                                styles.item,
                                item.destructive && styles.itemDestructive,
                            )}
                            disabled={item.disabled}
                            key={item.id}
                            onSelect={() => item.onSelect?.(item.id)}
                        >
                            {item.icon ? (
                                <span aria-hidden className={styles.itemIcon}>
                                    {item.icon}
                                </span>
                            ) : null}
                            {item.label}
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
});

ButtonDropdown.displayName = "ButtonDropdown";
